/**
 * The menu blob design was driven by the front-end menu-editing UI, which allowed
 * for rapid development but also means there's a lot of noise in the menu that
 * the UI (currently) needs but that doesn't impact business logic.
 *
 * Just about anywhere you can have more then one way of configuring
 * something all of the data for the un-used configuration is there.  For example,
 * when an offer comes out of the UI, all the configuration for individual-group
 * values is present even when it's an entire-order valued offer.
 *
 * This file implements a layer that sits between menu storage (in the TCC
 * backend) and the menu editing UI.  It has a dehydrateMenu function that removes
 * the unused configuration elements and a rehydrateMenu function that adds them
 * back in, so the front-end can continue to be written in the same style.
 *
 */
import { initOffer } from '../../menu/offers/offers-initializer'
import { emptyButton } from 'shared-js/default-sections/menu-layout-defaults'
import { deepClone } from './deep-clone'

export function dehydrateMenu(menu) {

  // TODO Enable once the NPE fixes in thrive-pricing have been distributed
  // menu.offers.forEach(dehydrateOffer)

  if (menu.layout && menu.layout.tabs) {
    Object.values(menu.layout.tabs)
      .forEach(tab => Object.values(tab.buttons).forEach(dehydrateMenuButton))
  }

  return menu
}

export function rehydrateMenu(menu) {
  if (menu.layout && menu.layout.tabs) {
    Object.values(menu.layout.tabs)
      .forEach(tab => Object.values(tab.buttons).forEach(rehydrateMenuButton))
  }

  return menu
}

//
// region Menu Buttons
//

function dehydrateMenuButton(button) {
  let type = button.type
  let ti = button.typeInfo

  if(type !== 'ITEM') {
    delete ti.itemInfo
  }
  if(type !== 'MODIFIER') {
    delete ti.modifierInfo
  }
  if(type !== 'SIZE') {
    delete ti.sizeInfo
  }
  if(type !== 'STYLE') {
    delete ti.styleInfo
  }
  if(type !== 'MESSAGE') {
    delete ti.messageId
  }
  if(type !== 'TENDER') {
    delete ti.tenderInfo
  }
  if(type !== 'MENU_LINK') {
    delete ti.menuLinkId
  }
  if(type !== 'SPECIAL_FUNCTION') {
    delete ti.specialFunctionInfo
  }
  if(type !== 'OFFER') {
    delete ti.offerInfo
  }
}

const EMPTY_BUTTON = emptyButton()

function rehydrateMenuButton(button) {
  repopulateChildren(button.typeInfo, EMPTY_BUTTON.typeInfo)
}

// endregion

//
// region Offers
//
// eslint-disable-next-line no-unused-vars
function dehydrateOffer(offer) {

  deleteDisabledChildren(offer.validity)
  deleteDisabledChildren(offer.interactions.notValidWithOffersThat)

  let appliesTo = offer.value.appliesTo

  offer.value.pricingSchemes.forEach(scheme => {
    if(appliesTo !== 'ENTIRE_ORDER') {
      delete scheme.entireOrder
    }
    if(appliesTo !== 'INDIVIDUAL_GROUPS') {
      delete scheme.individualGroups
    }
    if(appliesTo !== 'ITEM_COMBO') {
      delete scheme.comboOffer
    }
    if(appliesTo !== 'ANY_ITEM') {
      delete scheme.anyItem
    }
  })

  return offer
}


const EMPTY_OFFER = initOffer({}, () => '-1', '-1')

// eslint-disable-next-line no-unused-vars
function rehydrateOffer(offer) {

  repopulateChildren(offer.validity, EMPTY_OFFER.validity)
  repopulateChildren(
    offer.interactions.notValidWithOffersThat,
    EMPTY_OFFER.interactions.notValidWithOffersThat)

  const EMPTY_SCHEME = EMPTY_OFFER.value.pricingSchemes[0]

  offer.value.pricingSchemes.forEach(scheme => {
    repopulateChildren(scheme, EMPTY_SCHEME)
  })

  return offer
}

// endregion

//
// region Utilities
//

function repopulateChildren(dry, wet) {
  for(let [propName, propValue] of Object.entries(wet)) {
    if(!dry.hasOwnProperty(propName)) {
      dry[propName] = deepClone(propValue)
    }
  }
}

function deleteDisabledChildren(object) {
  for(let [propName, propValue] of Object.entries(object)) {
    if(typeof propValue === 'object') {
      deleteIfNotEnabled(object, propName)
    }
  }
}

function deleteIfNotEnabled(obj, propName) {
  if(obj.hasOwnProperty(propName)) {
    let prop = obj[propName]
    if(prop && prop.hasOwnProperty('enabled') && !prop.enabled) {
      delete obj[propName]
    }
  }
}

// endregion

